import { z } from 'zod';
import { amountInputSchema, amountSchema } from '../common/amount.js';
import { paginationQuerySchema } from '../common/pagination.js';
import { isoDateTimeSchema, localeSchema } from '../common/primitives.js';
import { actionStampSchema, bilingualTextSchema, branchRefSchema, currencyRefSchema, employeeRefSchema, } from '../common/refs.js';
import { tabletTaskViewSchema } from '../tablets/contract.js';
import { countryCodeSchema, documentNumberSchema, documentTypeSchema, fullNameSchema, intendedRecipientSchema, intendedRecipientViewSchema, optionalStoredPhoneSchema, partySnapshotSchema, signaturePartySchema, } from './parties.js';
import { storedPhoneSchema } from '../phone/phone.js';
/**
 * Deposits (cash-deposits.md sections 3, 4, 5 and 8).
 */
// ---------------------------------------------------------------------------
// States and references
// ---------------------------------------------------------------------------
export const DEPOSIT_STATES = [
    'DRAFT',
    'IN_CUSTODY',
    'DELIVERED',
    'RETURNED',
    'CANCELLED',
];
export const depositStateSchema = z.enum(DEPOSIT_STATES);
/** Final states: an ordinary edit never brings them back to IN_CUSTODY [COR-03]. */
export const CLOSED_DEPOSIT_STATES = ['DELIVERED', 'RETURNED', 'CANCELLED'];
/**
 * الرقم المرجعي: 10 random characters, non-guessable [DEP-05]. The alphabet
 * leaves out 0/O, 1/I/L and U so a typed reference is unambiguous.
 */
export const DEPOSIT_REFERENCE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';
export const DEPOSIT_REFERENCE_LENGTH = 10;
/** Normalises a typed or scanned reference: upper case, no spaces or dashes. */
export function normalizeDepositReference(input) {
    return input.replace(/[\s-]/g, '').toUpperCase();
}
export const depositReferenceSchema = z
    .string()
    .transform(normalizeDepositReference)
    .pipe(z
    .string()
    .regex(new RegExp(`^[${DEPOSIT_REFERENCE_ALPHABET}]{${DEPOSIT_REFERENCE_LENGTH}}$`), 'must be the 10-character reference printed under the QR code'));
/** Customers synchronisation of the deposit (بانتظار المزامنة until SYNCED). */
export const syncStatusSchema = z.enum(['PENDING', 'SYNCED']);
// ---------------------------------------------------------------------------
// Draft and confirmation (section 4.1)
// ---------------------------------------------------------------------------
const optionalText = (max) => z.string().trim().max(max).nullable().optional();
const depositDraftFieldsSchema = z.object({
    amount: amountInputSchema,
    currencyId: z.string().min(1),
    location: optionalText(200),
    notes: optionalText(1000),
    /**
     * العميل غير موجود (tablet.md section 12, DPS-48): nothing is sent to the
     * tablet and the deposit is confirmed without the depositor's data or
     * signature. Set only by the employee's choice.
     */
    customerNotPresent: z.boolean().optional(),
});
/**
 * POST /deposits/drafts — المبلغ، العملة، مكان الأمانة، ملاحظات، then إرسال إلى
 * التابلت. The tablet is required unless the customer is not present, and is
 * refused when they are not.
 */
export const createDepositDraftSchema = depositDraftFieldsSchema
    .extend({ tabletId: z.string().min(1).optional() })
    .superRefine((value, ctx) => {
    if (value.customerNotPresent === true && value.tabletId !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['tabletId'],
            message: 'must be empty when the customer is not present',
        });
    }
    if (value.customerNotPresent !== true && value.tabletId === undefined) {
        ctx.addIssue({ code: 'custom', path: ['tabletId'], message: 'is required' });
    }
});
/**
 * PATCH /deposits/drafts/:id — changing the amount or currency after the
 * depositor signed voids the signature and sends the form back to the tablet
 * [TAB-04, DPS-04]. Setting `customerNotPresent` cancels the form on the
 * tablet; clearing it leaves the draft waiting for إرسال إلى التابلت.
 */
export const updateDepositDraftSchema = depositDraftFieldsSchema
    .partial()
    .refine((value) => Object.keys(value).length > 0, 'at least one field is required');
/** POST /deposits/drafts/:id/send-to-tablet — send again, or to another tablet. */
export const sendDraftToTabletSchema = z.object({ tabletId: z.string().min(1) });
export const depositDraftViewSchema = z.object({
    id: z.string(),
    state: z.literal('DRAFT'),
    amount: amountSchema,
    currency: currencyRefSchema,
    branch: branchRefSchema,
    location: z.string().nullable(),
    notes: z.string().nullable(),
    created: actionStampSchema,
    /** العميل غير موجود: the draft is confirmed without a tablet task. */
    customerNotPresent: z.boolean(),
    /** The current tablet task (depositor form), with what the depositor entered. */
    task: tabletTaskViewSchema.nullable(),
    /** True right after an amount/currency change voided a signature. */
    signatureVoided: z.boolean(),
});
// ---------------------------------------------------------------------------
// Details (section 8.2)
// ---------------------------------------------------------------------------
export const signatureViewSchema = z.object({
    id: z.string(),
    party: signaturePartySchema,
    signedAt: isoDateTimeSchema,
    tabletName: z.string().nullable(),
    /** وُقّع على نسخة سابقة: the data changed after this signature. */
    superseded: z.boolean(),
});
export const documentPhotoViewSchema = z.object({
    id: z.string(),
    party: signaturePartySchema,
    takenAt: isoDateTimeSchema,
});
export const MESSAGE_TEMPLATES = [
    'deposit_created_depositor',
    'deposit_created_recipient',
    'deposit_delivered',
    'deposit_returned',
];
export const messageTemplateSchema = z.enum(MESSAGE_TEMPLATES);
/** بانتظار الإرسال، تم الإرسال (accepted, delivered or read)، فشل الإرسال [MSG-02]. */
export const MESSAGE_STATUSES = ['PENDING', 'SENT', 'FAILED'];
export const depositMessageStatusSchema = z.enum(MESSAGE_STATUSES);
export const MESSAGE_RECIPIENT_ROLES = [
    'DEPOSITOR',
    'INTENDED_RECIPIENT',
    'ACTUAL_RECIPIENT',
];
export const messageRecipientRoleSchema = z.enum(MESSAGE_RECIPIENT_ROLES);
export const depositMessageViewSchema = z.object({
    id: z.string(),
    template: messageTemplateSchema,
    /** One message goes out when two parties share a phone, so it may carry two roles. */
    roles: z.array(messageRecipientRoleSchema).min(1),
    phone: z.string(),
    language: localeSchema,
    status: depositMessageStatusSchema,
    /** Short reason from the WhatsApp service when FAILED. */
    error: z.string().nullable(),
    attempts: z.number().int().nonnegative(),
    createdAt: isoDateTimeSchema,
    sentAt: isoDateTimeSchema.nullable(),
    updatedAt: isoDateTimeSchema,
});
export const PRINT_OUTCOMES = ['PRINTED', 'FAILED'];
export const printOutcomeSchema = z.enum(PRINT_OUTCOMES);
export const printLogViewSchema = z.object({
    id: z.string(),
    reprint: z.boolean(),
    outcome: printOutcomeSchema,
    by: employeeRefSchema,
    at: isoDateTimeSchema,
});
/** Field names recorded in سجل التعديلات. */
export const DEPOSIT_EDIT_FIELDS = [
    'amount',
    'currency',
    'location',
    'notes',
    'depositor.fullName',
    'depositor.phone',
    'depositor.documentType',
    'depositor.documentNumber',
    'depositor.documentCountry',
    'intendedRecipient.fullName',
    'intendedRecipient.phone',
    'actualRecipient.fullName',
    'actualRecipient.phone',
    'actualRecipient.documentType',
    'actualRecipient.documentNumber',
    'actualRecipient.documentCountry',
];
export const depositEditFieldSchema = z.enum(DEPOSIT_EDIT_FIELDS);
export const depositEditViewSchema = z.object({
    id: z.string(),
    field: depositEditFieldSchema,
    oldValue: z.string().nullable(),
    newValue: z.string().nullable(),
    reason: z.string(),
    attachment: z.object({ id: z.string(), name: z.string() }).nullable(),
    by: employeeRefSchema,
    at: isoDateTimeSchema,
});
export const REFERRAL_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'];
export const referralStatusSchema = z.enum(REFERRAL_STATUSES);
export const referralDecisionSchema = z.enum(['APPROVED', 'REJECTED']);
export const referralViewSchema = z.object({
    id: z.string(),
    status: referralStatusSchema,
    employeeNote: z.string(),
    referred: actionStampSchema,
    /** The recipient data and signature as recorded when the delivery was referred. */
    recordedRecipient: z.object({
        fullName: z.string(),
        phone: z.string(),
        documentType: documentTypeSchema,
        documentNumber: z.string(),
        documentCountry: z.string(),
    }),
    signatureId: z.string().nullable(),
    decision: z
        .object({
        decision: referralDecisionSchema,
        note: z.string(),
        by: employeeRefSchema,
        at: isoDateTimeSchema,
    })
        .nullable(),
});
export const depositDetailsSchema = z.object({
    id: z.string(),
    /** Null while DRAFT: number and reference are given at confirmation. */
    number: z.number().int().positive().nullable(),
    reference: z.string().nullable(),
    state: depositStateSchema,
    amount: amountSchema,
    currency: currencyRefSchema,
    branch: branchRefSchema,
    location: z.string().nullable(),
    notes: z.string().nullable(),
    /** Language chosen by the depositor on the tablet; used for receipt and messages. */
    language: localeSchema,
    /** بانتظار مراجعة المشرف. */
    referralPending: z.boolean(),
    /** العميل غير موجود: confirmed without the depositor's data or signature. */
    customerNotPresent: z.boolean(),
    /** Null for a draft the depositor has not submitted yet, or when the customer is not present. */
    depositor: partySnapshotSchema.nullable(),
    intendedRecipient: intendedRecipientViewSchema.nullable(),
    /** The person who collected (DELIVERED) or the depositor as re-identified (RETURNED). */
    actualRecipient: partySnapshotSchema.nullable(),
    signatures: z.array(signatureViewSchema),
    /** Empty unless the user has DEPOSIT_DOCUMENT_VIEW. */
    documentPhotos: z.array(documentPhotoViewSchema),
    created: actionStampSchema,
    confirmed: actionStampSchema.nullable(),
    closed: actionStampSchema.nullable(),
    cancelReason: z.string().nullable(),
    syncStatus: syncStatusSchema,
    /**
     * A final Customers refusal of a verified phone change, shown on the
     * deposit: PHONE_TAKEN «رفض نظام العملاء هذا الرقم، عدّل رقم الهاتف»
     * (customers.md section 10.4). Null otherwise.
     */
    customerLinkError: z.enum(['PHONE_TAKEN']).nullable(),
    messages: z.array(depositMessageViewSchema),
    prints: z.array(printLogViewSchema),
    edits: z.array(depositEditViewSchema),
    referrals: z.array(referralViewSchema),
});
// ---------------------------------------------------------------------------
// List (section 8.1)
// ---------------------------------------------------------------------------
export const DEPOSIT_LIST_SORT_FIELDS = ['number', 'depositedAt', 'closedAt', 'amount'];
/**
 * `state` defaults to IN_CUSTODY [LIST-01]. `ALL` means every confirmed deposit
 * except CANCELLED; drafts and cancelled deposits appear only when chosen.
 */
export const depositListStateFilterSchema = z.enum([...DEPOSIT_STATES, 'ALL']);
const booleanQuery = z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => (value === undefined ? undefined : value === 'true'));
/** Filters shared by the list, its exports and the reports. */
export const depositFilterSchema = z.object({
    /** Search: number, reference, customer name or phone. */
    search: z.string().trim().max(200).optional(),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    /** نوع التاريخ: deposit (confirmation) or delivery (closing) date. */
    dateType: z.enum(['DEPOSIT', 'DELIVERY']).default('DEPOSIT'),
    state: depositListStateFilterSchema.default('IN_CUSTODY'),
    branchId: z.string().min(1).optional(),
    currencyId: z.string().min(1).optional(),
    depositEmployeeId: z.string().min(1).optional(),
    deliveryEmployeeId: z.string().min(1).optional(),
    returnEmployeeId: z.string().min(1).optional(),
    referralPending: booleanQuery,
});
export const depositListQuerySchema = paginationQuerySchema
    .extend(depositFilterSchema.shape)
    .extend({ sortBy: z.enum(DEPOSIT_LIST_SORT_FIELDS).optional() });
export const EXPORT_FORMATS = ['xlsx', 'pdf'];
export const exportFormatSchema = z.enum(EXPORT_FORMATS);
/** GET /deposits/export — same filters, a format and the label language. */
export const depositExportQuerySchema = depositListQuerySchema
    .omit({ page: true, pageSize: true })
    .extend({ format: exportFormatSchema, lang: localeSchema.default('ar') });
export const depositListItemSchema = z.object({
    id: z.string(),
    number: z.number().int().positive().nullable(),
    reference: z.string().nullable(),
    state: depositStateSchema,
    /** Confirmation time (creation time for a draft). */
    depositedAt: isoDateTimeSchema,
    closedAt: isoDateTimeSchema.nullable(),
    branch: branchRefSchema,
    amount: amountSchema,
    currency: currencyRefSchema,
    depositorName: z.string().nullable(),
    depositorPhone: z.string().nullable(),
    /** العميل غير موجود: shown in place of the empty depositor. */
    customerNotPresent: z.boolean(),
    /** The actual recipient once delivered, otherwise the intended recipient. */
    recipientName: z.string().nullable(),
    recipientKind: z.enum(['INTENDED', 'ACTUAL']).nullable(),
    referralPending: z.boolean(),
    depositEmployee: employeeRefSchema.nullable(),
    /** The employee who delivered; null unless the deposit is DELIVERED. */
    deliveryEmployee: employeeRefSchema.nullable(),
    /** The employee who returned it to the depositor; null unless the deposit is RETURNED. */
    returnEmployee: employeeRefSchema.nullable(),
    syncStatus: syncStatusSchema,
});
// ---------------------------------------------------------------------------
// Receipt and printing (section 4.2)
// ---------------------------------------------------------------------------
/** GET /deposits/:id/receipt — everything the 80 mm receipt prints. */
export const depositReceiptSchema = z.object({
    language: localeSchema,
    organisationName: bilingualTextSchema,
    receiptHeader: bilingualTextSchema,
    receiptFooter: bilingualTextSchema,
    confirmedAt: isoDateTimeSchema,
    branch: branchRefSchema,
    number: z.number().int().positive(),
    amount: amountSchema,
    currency: currencyRefSchema,
    depositorName: z.string(),
    intendedRecipientName: z.string().nullable(),
    /** The employee who received the deposit (name snapshot); null when no name is known. */
    depositEmployee: z.object({ nameAr: z.string(), nameEn: z.string() }).nullable(),
    /** The declaration the depositor accepted, in the language shown; null when none was recorded. */
    declaration: z.string().nullable(),
    reference: z.string(),
    /** PNG of the QR, base64 without a data: prefix. It encodes the reference only [QR-01]. */
    qrPngBase64: z.string(),
});
/** POST /deposits/:id/prints — record the print result (reprint needs DEPOSIT_REPRINT). */
export const recordPrintSchema = z.object({
    reprint: z.boolean(),
    outcome: printOutcomeSchema,
});
// ---------------------------------------------------------------------------
// Delivery, return and referral (section 5)
// ---------------------------------------------------------------------------
/** GET /deliveries/lookup?reference= — scanning or typing changes nothing. */
export const deliveryLookupQuerySchema = z.object({ reference: depositReferenceSchema });
export const deliveryLookupSchema = z.object({
    id: z.string(),
    number: z.number().int().positive(),
    reference: z.string(),
    amount: amountSchema,
    currency: currencyRefSchema,
    branch: branchRefSchema,
    depositedAt: isoDateTimeSchema,
    /**
     * Snapshot, for comparison when returning to the depositor; null for a
     * deposit confirmed with العميل غير موجود.
     */
    depositor: partySnapshotSchema.nullable(),
    customerNotPresent: z.boolean(),
    intendedRecipient: intendedRecipientViewSchema.nullable(),
    referralPending: z.boolean(),
    /** The latest referral, so an approved one lets the delivery proceed. */
    latestReferral: referralViewSchema.nullable(),
});
/**
 * `error.data` of the refusals [DEL-02]:
 * - DEPOSIT_NOT_IN_CUSTODY (409): who closed it and when.
 * - DEPOSIT_OTHER_BRANCH (409): the branch that holds it.
 */
export const depositNotInCustodyDataSchema = z.object({
    state: depositStateSchema,
    closedBy: employeeRefSchema.nullable(),
    closedAt: isoDateTimeSchema.nullable(),
});
export const depositOtherBranchDataSchema = z.object({ branch: branchRefSchema });
/** POST /deposits/:id/recipient-capture — send the recipient (or return) form to the tablet. */
export const recipientCaptureSchema = z.object({
    tabletId: z.string().min(1),
    purpose: z.enum(['DELIVERY', 'RETURN']),
});
/**
 * The id of a verified WhatsApp phone verification (customers.md section
 * 10.4), sent with a save when the switch of إعدادات الأمانات is on and the
 * phone needs a code. The API consumes it inside the save.
 */
export const phoneVerificationIdSchema = z.string().trim().min(1).max(64);
/** POST /deposits/drafts/:id/confirm (with an Idempotency-Key): تأكيد الإيداع. */
export const confirmDepositSchema = z
    .object({ phoneVerificationId: phoneVerificationIdSchema.optional() })
    .optional()
    .transform((value) => value ?? {});
/** POST /deposits/:id/deliver and /deposits/:id/return (with an Idempotency-Key). */
export const closeDepositSchema = z.object({
    taskId: z.string().min(1),
    phoneVerificationId: phoneVerificationIdSchema.optional(),
});
const noteSchema = z.string().trim().min(1).max(1000);
/** POST /deposits/:id/referrals — إحالة للمشرف, a note is required. */
export const referDepositSchema = z.object({
    taskId: z.string().min(1),
    note: noteSchema,
});
/** POST /deposits/:id/referrals/:referralId/review — قرار المشرف with a note. */
export const reviewReferralSchema = z.object({
    decision: referralDecisionSchema,
    note: noteSchema,
});
// ---------------------------------------------------------------------------
// Edit and cancel (sections 8.3 and 8.4)
// ---------------------------------------------------------------------------
/**
 * A party of an edited deposit. Its phone is read with `storedPhoneSchema`:
 * an unchanged stored phone of an excluded country saves, a changed one is
 * refused by the edit service (customers.md section 9.7).
 */
const partialIdentitySchema = z
    .object({
    fullName: fullNameSchema,
    phone: storedPhoneSchema,
    documentType: documentTypeSchema,
    documentNumber: documentNumberSchema,
    documentCountry: countryCodeSchema,
})
    .partial();
/**
 * PATCH /deposits/:id (with an Idempotency-Key). Descriptive fields are
 * ordinary edits; amount, currency, a party's identity and the intended
 * recipient are substantive and allowed only while IN_CUSTODY. On a DELIVERED
 * or RETURNED deposit only `actualRecipient` may change [COR-03].
 */
export const editDepositSchema = z
    .object({
    reason: z.string().trim().min(1).max(1000),
    /** From POST /deposits/:id/edit-attachments. */
    attachmentFileId: z.string().min(1).nullable().optional(),
    amount: amountInputSchema.optional(),
    currencyId: z.string().min(1).optional(),
    location: optionalText(200),
    notes: optionalText(1000),
    depositor: partialIdentitySchema.optional(),
    intendedRecipient: intendedRecipientSchema
        .extend({ phone: optionalStoredPhoneSchema.optional() })
        .nullable()
        .optional(),
    actualRecipient: partialIdentitySchema.optional(),
    /** A changed phone of a registered party, verified while the switch is on. */
    phoneVerificationId: phoneVerificationIdSchema.optional(),
})
    .refine((value) => Object.keys(value).some((key) => key !== 'reason' && key !== 'attachmentFileId' && key !== 'phoneVerificationId'), 'at least one field must change');
/** Edit attachment: multipart field `file`; PDF, JPEG or PNG up to 5 MB. */
export const EDIT_ATTACHMENT_CONTENT_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
];
export const EDIT_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
export const editAttachmentSchema = z.object({ fileId: z.string(), name: z.string() });
/** POST /deposits/:id/resign — send the edited deposit to the tablet for a new signature. */
export const resignDepositSchema = z.object({ tabletId: z.string().min(1) });
/** POST /deposits/:id/cancel — reason required, and the supervisor confirms no cash is held. */
export const cancelDepositSchema = z.object({
    reason: z.string().trim().min(1).max(1000),
    confirmNoCashHeld: z.literal(true),
});
//# sourceMappingURL=contract.js.map